import type { IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import React, { type ReactElement } from 'react';
import type { Placement } from '../Tooltip';
import { TooltipBubble } from '../TooltipBubble';
import { TextButton } from '../Typography';

export interface RadioGroupButtonConfig<T> {
  id: T;
  leftIcon?: React.ReactElement<IconProps>;
  rightIcon?: React.ReactElement<IconProps>;
  icon?: React.ReactElement<IconProps>;
  label?: string;
  name?: string;
  small?: boolean;
  disabled?: boolean;
  tooltip?: boolean;
  tooltipPlacement?: Placement;
  tooltipTitle?: string;
}

export interface RadioGroupButtonItemProps<T> {
  onClick?: () => void;
  selected?: boolean;
  buttonConfig?: RadioGroupButtonConfig<T>;
  small?: boolean;
  theme?: 'dark' | 'light';
}

export const RadioGroupButtonItem = <T,>({
  onClick,
  selected = false,
  buttonConfig,
  small,
  theme = 'light',
}: RadioGroupButtonItemProps<T>) => {
  const {
    leftIcon,
    rightIcon,
    icon,
    label,
    name = 'radio',
    id,
    disabled = false,
    tooltip,
    tooltipPlacement = 'top',
    tooltipTitle = '',
  } = buttonConfig ?? {};

  const renderIcon = (icon: ReactElement<IconProps>) =>
    React.cloneElement(icon, { size: 20 });

  return (
    <div className="group">
      <TooltipBubble
        placement={tooltipPlacement}
        title={tooltipTitle}
        disabled={!tooltip}
      >
        <input
          className="hidden"
          id={String(id)}
          type="radio"
          name={name}
          checked={selected}
          readOnly
        />
        <div
          className={classNames(
            `
            flex items-center justify-center gap-2 whitespace-nowrap mx-[-0.5px]
            outline outline-1 outline-offset-[-1px] outline-ds-natural-150
            group-first:rounded-l-md group-first:ml-0 group-last:rounded-r-md group-last:mr-0
            `,
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            small ? 'py-ds-1.25' : 'py-2',
            icon ? 'px-2' : 'px-4',
            theme === 'light' &&
              `
            text-ds-natural-600
            bg-none
            hover:bg-ds-natural-50

            aria-checked:text-ds-primary-300 
            aria-checked:bg-ds-natural-100
            aria-checked:hover:bg-ds-natural-100
            
            aria-disabled:text-ds-natural-200
            aria-disabled:hover:text-ds-natural-200
            `,
            theme === 'dark' &&
              `
            text-ds-accent3-100
            bg-none
            hover:bg-ds-accent3-100/25

            aria-checked:text-ds-primary-300
            aria-checked:bg-ds-natural-100
            aria-checked:hover:bg-ds-natural-100

            aria-disabled:text-ds-natural-400
            aria-disabled:hover:text-ds-natural-400
            `
          )}
          onClick={() => !disabled && onClick?.()}
          role="radio"
          aria-checked={selected}
          aria-disabled={disabled}
          aria-label={tooltipTitle ?? name}
        >
          {icon && renderIcon(icon)}
          {leftIcon && renderIcon(leftIcon)}
          {label && (
            <TextButton
              label={label}
              color="text-current"
            />
          )}
          {rightIcon && renderIcon(rightIcon)}
        </div>
      </TooltipBubble>
    </div>
  );
};
